import React, { useRef } from "react";
import {
  PrinterOutlined,
  DownloadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ReactToPrint from "react-to-print";
import { toast } from "react-toastify";

import { SpecialTokenResponse } from "../../../services/specialToken";
import ModalContainer from "../CustomModal/Modal";
import { downloadReceipt } from "../../../helper/downloadHelper";
import SpecialTokenReceipt from "./SpecialTokenReceipt";
import "./SpecialTokenModal.scss";

interface SpecialTokenGeneratedModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  tokenData: SpecialTokenResponse | null;
  closeCallback?: () => void;
}

const SpecialTokenGeneratedModal: React.FC<SpecialTokenGeneratedModalProps> = ({
  showModal,
  setShowModal,
  tokenData,
  closeCallback,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setShowModal(false);
    closeCallback?.();
  };

  const handleDownloadReceipt = async () => {
    const receipt = receiptRef.current;
    if (!receipt || !tokenData) return;

    try {
      await downloadReceipt(
        receipt,
        `engineering-token-${tokenData.meterNo || "receipt"}.pdf`
      );
    } catch {
      toast.error("Unable to download the token receipt");
    }
  };

  return (
    <>
      <ModalContainer
        showModal={showModal && Boolean(tokenData)}
        setShowModal={setShowModal}
        width={760}
        showTopCancelButton={false}
        maskStyle={{ backdropFilter: "blur(6px)" }}
        className="!max-w-[calc(100vw-16px)] !p-0 !pt-0 !pb-0 !overflow-visible [&_.ant-modal-content]:!overflow-visible [&_.ant-modal-content]:!rounded-[28px] [&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-content]:!shadow-none [&_.ant-modal-body]:!p-0"
      >
        {tokenData && (
          <div className="flex flex-row max-w-full items-start gap-3 max-[600px]:gap-2 max-[600px]:flex-col max-[360px]:flex-wrap max-[360px]:justify-center">
            <section className="lg:w-[min(560px,calc(100vw-140px))] shrink-0 rounded-[28px] bg-white px-8 pb-8 pt-12 shadow-xl max-[600px]:w-full max-[600px]:rounded-[18px] max-[600px]:px-3 max-[600px]:pb-3 max-[600px]:pt-5">
              <h2 className="mb-8 mt-0 text-center text-[20px] font-bold leading-7 text-[#202020] max-[600px]:mb-4 max-[600px]:text-[12px] max-[600px]:leading-4">
                Token successfully generated
              </h2>
              <SpecialTokenReceipt ref={receiptRef} tokenData={tokenData} />
            </section>
            <div className="flex w-[112px] flex-col gap-3 max-[600px]:w-[66px] max-[600px]:flex-row max-[600px]:gap-2">
              <button type="button" onClick={handleClose} aria-label="Close" className="flex h-[30px] w-[30px] -translate-x-0.5 items-center justify-center self-start rounded-full bg-white text-[#333] shadow-sm hover:bg-[#f2f2f2] max-[600px]:h-[20px] max-[600px]:min-w-[20px]">
                <CloseOutlined className="text-[12px] max-[600px]:text-[8px]" />
              </button>
              <ReactToPrint
                content={() => receiptRef.current}
                documentTitle={`engineering-token-${tokenData.meterNo || "receipt"}`}
                bodyClass="special-token-print"
                onPrintError={() => toast.error("Unable to print the token receipt")}
                trigger={() => (
                  <button type="button" className="flex h-[36px] w-full items-center gap-2 rounded-[7px] bg-white px-3 text-[#222] shadow-sm hover:bg-[#f3f3f3] max-[600px]:h-[22px] max-[600px]:gap-1 max-[600px]:rounded-[5px] max-[600px]:px-2">
                    <PrinterOutlined className="text-[12px] max-[600px]:text-[8px]" />
                    <span className="text-[12px] font-medium max-[600px]:text-[6px]">Print</span>
                  </button>
                )}
              />
              <button type="button" onClick={handleDownloadReceipt} className="flex h-[36px] w-full items-center gap-2 rounded-[7px] bg-white px-3 text-[#222] shadow-sm hover:bg-[#f3f3f3] max-[600px]:h-[22px] max-[600px]:gap-1 max-[600px]:rounded-[5px] max-[600px]:px-2">
                <DownloadOutlined className="text-[12px] max-[600px]:text-[8px]" />
                <span className="text-[12px] font-medium max-[600px]:text-[6px]">Download</span>
              </button>
            </div>
          </div>
        )}
      </ModalContainer>
    </>
  );
};

export default SpecialTokenGeneratedModal;
